import {
  DEFAULT_INVENTORY_SOURCE,
  InventorySourceSchema,
} from "@redotech/redo-fulfillment-group-common/inventory-source";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { transferSessionIdFormat } from "@redotech/redo-warehouse-id";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { PickSessionStrategy, ScanType } from "../pick-session-enums";

export enum PickSessionStatus {
  CREATING = "creating",
  ACTIVE = "active",
  PICKED = "picked",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  PRINTED = "printed",
  PROBLEM = "problem",
  // Continuation session created by the zone-handoff split: an upstream
  // zone's picker finished and the leftover items rolled into this session,
  // tagged with `zoneId = destination zone`. Sits idle until a picker at
  // that zone claims it (via the pickup popup or absorption into a fresh
  // wave) and the status flips to ACTIVE.
  HANDED_OFF = "handed_off",
  // Terminal state for a HANDED_OFF session whose items were rolled into
  // another session at claim time (the new wave's "primary" session).
  // Items live on the primary now; `consolidatedIntoPickSessionId` points
  // there. Distinct from CANCELLED because the picker didn't cancel the
  // work — it was consolidated forward.
  CONSOLIDATED = "consolidated",
}

export const defaultStrategyOrderCount: Record<PickSessionStrategy, number> = {
  [PickSessionStrategy.SINGLE_ITEM]: 30,
  [PickSessionStrategy.MULTI_ITEM]: 8,
};

export const PickSessionItemSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  fulfillmentGroupId: z.string().meta({ privacy: privacy.nonPii }),
  orderName: z.string().nullish().meta({ privacy: privacy.nonPii }),
  imageUrl: z.string().nullish().meta({ privacy: privacy.nonPii }),
  sku: z.string().nullish().meta({ privacy: privacy.nonPii }),
  customAttributes: z
    .array(
      z.object({
        key: z.string().meta({ privacy: privacy.nonPii }),
        value: z.string().meta({ privacy: privacy.free_text }),
      }),
    )
    .meta({ privacy: privacy.structured }),
  totalQuantity: z.number().meta({ privacy: privacy.nonPii }),
  quantityPicked: z.number().meta({ privacy: privacy.nonPii }),
  quantityOutOfStock: z.number().meta({ privacy: privacy.nonPii }),
  quantityByTote: z
    .record(
      zExt.objectId(),
      z
        .object({ quantity: z.number().meta({ privacy: privacy.nonPii }) })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.null }),
  // Picker-entered lots (non-WMS-SOT): toteId -> inventoryItemId (the lot)
  // -> {lotNumber, quantity}. `lotNumber` below stays the system FEFO lot.
  lotQuantityByTote: z
    .record(
      zExt.objectId(),
      z.record(
        z.string(),
        z
          .object({
            lotNumber: z.string().meta({ privacy: privacy.redact }),
            quantity: z.number().meta({ privacy: privacy.nonPii }),
          })
          .meta({ privacy: privacy.structured }),
      ),
    )
    .nullish()
    .meta({ privacy: privacy.null }),
  rawBin: z.string().nullish().meta({ privacy: privacy.nonPii }),
  bin: z.string().nullish().meta({ privacy: privacy.nonPii }),
  secondaryBins: z
    .array(z.string())
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  hasCoordinate: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  productTitle: z.string().meta({ privacy: privacy.nonPii }),
  variantTitle: z.string().meta({ privacy: privacy.nonPii }),
  variantId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  barcode: z.string().nullish().meta({ privacy: privacy.nonPii }),
  barcodes: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  externalId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  productCustomString1: z.string().nullish().meta({ privacy: privacy.nonPii }),
  productCustomString2: z.string().nullish().meta({ privacy: privacy.nonPii }),
  productCustomString3: z.string().nullish().meta({ privacy: privacy.nonPii }),
  variantCustomString1: z.string().nullish().meta({ privacy: privacy.nonPii }),
  variantCustomString2: z.string().nullish().meta({ privacy: privacy.nonPii }),
  variantCustomString3: z.string().nullish().meta({ privacy: privacy.nonPii }),
  vendor: z.string().nullish().meta({ privacy: privacy.nonPii }),
  skipIndex: z.number().nullish().meta({ privacy: privacy.nonPii }),
  storageLocationId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  productId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  // True when this item is on the pick despite lacking enough pickable
  // inventory to fulfill it, because it is allowed by the team's
  // allowed-problem-pick policy. The item may have no bin at all, a bin with
  // too little/no stock, or stock that is reserved for problem orders — so
  // storageLocationId may or may not be set.
  allowedProblemPickByPolicy: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  lotNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
  expirationDate: z.coerce.date().nullish().meta({ privacy: privacy.nonPii }),
  inventoryLevelId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  inventoryItemId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  orderNote: z.string().nullish().meta({ privacy: privacy.free_text }),
  // Append-only log of every pick action against this item. Each entry
  // attributes a slice of `quantityPicked` to the picker who put it in a
  // tote at that moment; an un-pick is a negative `quantity` entry that
  // comes off the newest lot first. Sum of `quantity` across entries SHOULD
  // equal `quantityPicked` — writes are best-effort so the validator does
  // NOT enforce the sum. Treat pickEvents as an audit aid. Multiple events
  // arise when an item is picked in stages, moves between sessions
  // (OOS restock, zone-handoff continuation), or is picked into
  // multiple totes. Events are immutable once written.
  pickEvents: z
    .array(
      z
        .object({
          quantity: z.number().meta({ privacy: privacy.nonPii }),
          pickedBy: zExt.objectId().meta({ privacy: privacy.nonPii }),
          pickedAt: z.coerce.date().meta({ privacy: privacy.nonPii }),
          pickedInPickSessionId: zExt
            .objectId()
            .meta({ privacy: privacy.nonPii }),
          toteId: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
          // Picker-entered lot active at the time of this pick (audit only).
          inventoryItemId: z
            .string()
            .nullish()
            .meta({ privacy: privacy.nonPii }),
          lotNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  // Zone the item's bin resolves to. Set when the wave is generated under
  // sequential zone picking; used to (a) filter the picker's view to only
  // items in the session's zone, (b) gate session-advance/complete checks
  // to in-scope items, and (c) route deferred items to the right
  // continuation session at split time. Null on items in non-zone-routed
  // sessions — pairs with a null `PickSession.zoneId` to leave existing
  // behavior unchanged.
  zoneId: z.string().nullish().meta({ privacy: privacy.nonPii }),
});
export type PickSessionItem = z.infer<typeof PickSessionItemSchema>;
export interface IPickSessionItemSchema extends z.ZodType<PickSessionItem> {}

export const PickSessionStatsSchema = z.object({
  averageDistanceFromSeed: z.number().meta({ privacy: privacy.nonPii }),
});
export type PickSessionStats = z.infer<typeof PickSessionStatsSchema>;
export interface IPickSessionStatsSchema extends z.ZodType<PickSessionStats> {}

export const PickSessionBaseSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  crdbLocationId: locationIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  updatedAt: z.date().meta({ privacy: privacy.nonPii }),
  user: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
  // Set when the session was explicitly queued for the assignee by another
  // action (e.g. bulk-create-pick-session-from-FG-table). Null for sessions
  // a user creates for themselves via the WMS pick-session create flow.
  // Used to render "New assignment" vs "Resume" buckets on the pick-sessions
  // landing page: a session with `assigningUser != null` and zero items
  // picked is treated as a new assignment.
  assigningUser: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
  status: createEnum(PickSessionStatus, { enumName: "PickSessionStatus" }).meta(
    { privacy: privacy.nonPii },
  ),
  scanType: createEnum(ScanType, { enumName: "ScanType" }).meta({
    privacy: privacy.nonPii,
  }),
  items: z.array(PickSessionItemSchema).meta({ privacy: privacy.structured }),
  // Read-only audit of items stripped from `items[]` at split time
  // (handoff OR OOS). Picker UI / mutation paths operate on `items`
  // only; this is admin/reporting only. Invariant: items + historicalItems
  // = every item this session was ever responsible for.
  historicalItems: z
    .array(PickSessionItemSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  stats: PickSessionStatsSchema.meta({ privacy: privacy.structured }),
  strategy: createEnum(PickSessionStrategy, {
    enumName: "PickSessionStrategy",
  }),
  inventorySource: InventorySourceSchema.default(DEFAULT_INVENTORY_SOURCE).meta(
    { privacy: privacy.nonPii },
  ),
  completedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  pickedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  printedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  sourcingCompletedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  filterName: z.string().nullish().meta({ privacy: privacy.nonPii }),
  fullTotes: z.array(z.string()).nullish().meta({ privacy: privacy.nonPii }),
  markedProblemBy: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
  transferSessionId: transferSessionIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  // Zone the session is rooted in for sequential zone picking. The picker
  // assigned to this session works `zoneId` exclusively; FGs whose first
  // zone is `zoneId` enter here, and continuation sessions for downstream
  // zones are spawned at session completion (handoff split flow lands in a
  // follow-up PR). Null on all sessions for teams with
  // `zonePickingMode = NONE` — the existing flow is unchanged.
  zoneId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  // Backwards pointer set when this session was spawned at handoff split
  // time from `markPickSessionAsPicked` of the upstream zone's session.
  // Combined with `consolidatedIntoPickSessionId`, lets callers walk an
  // FG's full pick history forward or backward across zones. Null on
  // sessions created via the normal wave flow (no upstream parent).
  continuedFromPickSessionId: zExt
    .objectId()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  // Forward pointer set when a HANDED_OFF session was consolidated into
  // a new wave's primary session at claim time. Status flips to
  // CONSOLIDATED and items live on the primary; this pointer says where
  // to look. Null on every other session.
  consolidatedIntoPickSessionId: zExt
    .objectId()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  // Populated on the *parent* session at split time (when
  // `markPickSessionAsPicked` runs and creates continuation sessions for
  // deferred items). Drives the end-of-pick "place these totes for the
  // next zone" modal: one entry per tote that carries items into another
  // zone. The continuation session(s) themselves can be looked up by
  // walking `continuedFromPickSessionId == thisSession._id`. Null on
  // sessions that produced no handoffs (every item was either picked or
  // out-of-stock in this session's zone).
  toteHandoffs: z
    .array(
      z
        .object({
          toteId: z.string().meta({ privacy: privacy.nonPii }),
          destinationZoneId: z.string().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  // Picker-entered active lot per productId, applied to subsequent picks.
  // "Change lot number" overwrites a key; earlier picks keep their lot.
  activeLotsByProduct: z
    .record(
      z.string(),
      z
        .object({
          inventoryItemId: z.string().meta({ privacy: privacy.nonPii }),
          lotNumber: z.string().meta({ privacy: privacy.redact }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.null }),
});

const SingleItemPickSessionSchema = PickSessionBaseSchema.extend({
  strategy: z
    .literal(PickSessionStrategy.SINGLE_ITEM)
    .meta({ privacy: privacy.nonPii }),
});

const MultiItemPickSessionSchema = PickSessionBaseSchema.extend({
  strategy: z
    .literal(PickSessionStrategy.MULTI_ITEM)
    .meta({ privacy: privacy.nonPii }),
});

// const BulkShipmentPickSessionSchema = PickSessionBaseSchema.extend({
//   strategy: z.literal(PickSessionStrategy.BULK_SHIPMENT),
// });

export const PickSessionSchema = z.discriminatedUnion("strategy", [
  SingleItemPickSessionSchema,
  MultiItemPickSessionSchema,
  // BulkShipmentPickSessionSchema,
]);

export type PickSession = z.infer<typeof PickSessionSchema>;
export interface IPickSessionSchema extends z.ZodType<PickSession> {}

export function formatPickSessionStrategy(
  strategy: PickSessionStrategy,
): string {
  switch (strategy) {
    case PickSessionStrategy.SINGLE_ITEM:
      return "Single item";
    case PickSessionStrategy.MULTI_ITEM:
      return "Multi item";
    // case PickSessionStrategy.BULK_SHIPMENT:
    //   return "Bulk Shipment Orders";
    default:
      assertNever(strategy);
  }
}

export function ensureValidPickSession({
  pickSession,
}: {
  pickSession: PickSession;
}): { valid: boolean; message?: string } {
  // all same tote for single item
  // 1 unique tote per order multi item
  // tote quant not greater than available quant (total - out of stock)
  // picked quant not greater than total quant
  // out of stock quant not negative
  // picked + out of stock not greater than total quant

  // Zone-mode parity between session and items. Catches writer drift where
  // one side gained a zone tag but the other didn't. Non-zone sessions
  // (zoneId == null on session and every item) are unchanged.
  const sessionIsZoneRouted = pickSession.zoneId != null;
  for (const item of pickSession.items) {
    const itemIsZoneTagged = item.zoneId != null;
    if (sessionIsZoneRouted && !itemIsZoneTagged) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has no zoneId on a zone-routed pick session (zoneId=${pickSession.zoneId})`,
      };
    }
    if (!sessionIsZoneRouted && itemIsZoneTagged) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has zoneId=${item.zoneId} on a non-zone-routed pick session`,
      };
    }
  }

  // Single-item zone-routed sessions: every item must belong to the
  // session's zone. Multi-item sessions are allowed to carry items
  // from later zones as "deferred" — those get split off into
  // continuation sessions and are validated by the deferred check
  // further below — but single-item is one FG per session by
  // definition, so a cross-zone item can only mean the writer skipped
  // the zone filter (see the `candidateGroups` / fallback path in
  // `get-pick-wave.ts`). Defense in depth: reject here so a regression
  // in the wave builder surfaces immediately rather than silently
  // shipping a misrouted session to the picker.
  //
  // Cancellation explicitly skips this validator (see
  // `cancelPickSessionMongo` in `pick-session.ts`) so already-bad
  // sessions can still be torn down; completion DOES enforce it.
  if (
    sessionIsZoneRouted &&
    pickSession.strategy === PickSessionStrategy.SINGLE_ITEM
  ) {
    for (const item of pickSession.items) {
      if (item.zoneId != null && item.zoneId !== pickSession.zoneId) {
        return {
          valid: false,
          message: `Single-item pick session in zone ${pickSession.zoneId} contains item ${item._id} (group ${item.fulfillmentGroupId}) from zone ${item.zoneId}`,
        };
      }
    }
  }

  for (const item of pickSession.items) {
    // Out-of-scope items (zone-routed sessions only): the item's zoneId
    // doesn't match this session's zoneId, which on a continuation means
    // either (a) it was picked upstream and carried forward with its
    // original zone tag, or (b) it's deferred to a downstream zone and
    // will be split off when this session completes.
    //
    // Defense-in-depth: no `pickEvent` on an out-of-scope item may point
    // back to this session. Any pick attributed to this session implies
    // a writer bug — the picker UI scopes to in-scope items via
    // `pickSessionItemsInScope`, so a quantity bump on an out-of-scope
    // item could only come from a mutation path that skipped the scope
    // filter. Upstream-attributed events (pickedInPickSessionId !== this
    // session) are fine — that's exactly how carryover works. The check
    // is a no-op for non-zone sessions because every item is in scope.
    if (sessionIsZoneRouted && !pickSessionItemIsInScope(pickSession, item)) {
      for (const event of item.pickEvents ?? []) {
        if (
          event.pickedInPickSessionId.toString() === pickSession._id.toString()
        ) {
          return {
            valid: false,
            message: `Item ${item._id} group ${item.fulfillmentGroupId} is out-of-scope (zone ${item.zoneId}) on session in zone ${pickSession.zoneId} but has a pickEvent attributed to this session`,
          };
        }
      }
    }

    // Validate picked quantity not greater than total quantity
    if (item.quantityPicked > item.totalQuantity) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has picked quantity (${item.quantityPicked}) greater than total quantity (${item.totalQuantity})`,
      };
    }

    // Validate out of stock quantity is not negative
    if (item.quantityOutOfStock < 0) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has negative out of stock quantity (${item.quantityOutOfStock})`,
      };
    }

    // Validate that picked + out of stock doesn't exceed total quantity
    const pickedPlusOutOfStock = item.quantityPicked + item.quantityOutOfStock;
    if (pickedPlusOutOfStock > item.totalQuantity) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has picked (${item.quantityPicked}) + out of stock (${item.quantityOutOfStock}) = ${pickedPlusOutOfStock} greater than total quantity (${item.totalQuantity})`,
      };
    }

    // Validate tote quantities if they exist
    if (item.quantityByTote) {
      let totalToteQuantity = 0;
      for (const [, tote] of Object.entries(item.quantityByTote)) {
        totalToteQuantity += tote.quantity;
      }

      // Validate tote quantity not greater than picked quantity
      if (totalToteQuantity > item.quantityPicked) {
        return {
          valid: false,
          message: `Item ${item._id} group ${item.fulfillmentGroupId} has tote quantities (${totalToteQuantity}) greater than picked quantity (${item.quantityPicked})`,
        };
      }

      // Validate tote quantity not greater than available quantity (total - out of stock)
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;
      if (totalToteQuantity > availableQuantity) {
        return {
          valid: false,
          message: `Item ${item._id} group ${item.fulfillmentGroupId} has tote quantities (${totalToteQuantity}) greater than available quantity (${availableQuantity} = total ${item.totalQuantity} - out of stock ${item.quantityOutOfStock})`,
        };
      }
    }

    // For scan_at_cart with multi_item strategy, validate that picked quantities match tote assigned quantities
    // Single item strategy allows skipping tote assignment, so picked items may not have totes assigned
    if (
      pickSession.scanType === ScanType.SCAN_AT_CART &&
      pickSession.strategy === PickSessionStrategy.MULTI_ITEM
    ) {
      const toteQuantity = item.quantityByTote
        ? Object.values(item.quantityByTote).reduce(
            (sum, tote) => sum + tote.quantity,
            0,
          )
        : 0;

      if (item.quantityPicked !== toteQuantity) {
        return {
          valid: false,
          message: `For scan_at_cart, item ${item._id} group ${item.fulfillmentGroupId} has picked quantity (${item.quantityPicked}) that doesn't match tote assigned quantity (${toteQuantity})`,
        };
      }
    }
  }

  // For multi-item strategy, validate that different fulfillmentGroupIds don't share the same tote
  if (pickSession.strategy === PickSessionStrategy.MULTI_ITEM) {
    const toteToFulfillmentGroup = new Map<string, string>();

    for (const item of pickSession.items) {
      if (item.quantityByTote) {
        for (const toteId of Object.keys(item.quantityByTote)) {
          const existingGroup = toteToFulfillmentGroup.get(toteId);
          if (existingGroup && existingGroup !== item.fulfillmentGroupId) {
            return {
              valid: false,
              message: `Multi item pick session cannot have different orders sharing the same tote. Tote ${toteId} is assigned to both fulfillment group ${existingGroup} and ${item.fulfillmentGroupId}`,
            };
          }
          toteToFulfillmentGroup.set(toteId, item.fulfillmentGroupId);
        }
      }
    }
  }

  return { valid: true };
}

/**
 * True when the item belongs to the picker's current zone scope.
 *
 * Backward-compat matrix:
 *   - pickSession.zoneId == null AND item.zoneId == null
 *       → TRUE. This is every existing document and every session on a
 *         team with `zonePickingMode === NONE` — behavior unchanged.
 *   - both set and equal → TRUE. Item belongs to the picker's zone.
 *   - mismatched → FALSE. Item is deferred to a later zone's continuation
 *         session and should be hidden from the current picker's view and
 *         excluded from completion checks.
 *
 * The asymmetric null cases (one side set, the other null) can't occur
 * if writers maintain the invariant "zone-routed session ⇒ every item
 * has a zoneId" — but the helper still returns FALSE for them, which is
 * the safe default (don't show an item to a picker we can't confirm
 * belongs to their zone).
 */
export function pickSessionItemIsInScope(
  pickSession: PickSession,
  item: PickSessionItem,
): boolean {
  return (item.zoneId ?? null) === (pickSession.zoneId ?? null);
}

/**
 * The subset of `pickSession.items` the current picker is responsible
 * for right now. For non-zone-routed sessions this returns every item,
 * matching pre-feature behavior exactly.
 */
export function pickSessionItemsInScope(
  pickSession: PickSession,
): PickSessionItem[] {
  return pickSession.items.filter((item) =>
    pickSessionItemIsInScope(pickSession, item),
  );
}

/**
 * Pure description of what a zone-handoff split should produce for a single
 * fulfillment group when the parent zone-routed session is being marked as
 * picked. Returned by `planZoneHandoffSplit`; consumed by the db-util layer
 * to drive Mongo writes (insert continuation, strip+archive items on parent,
 * repoint totes, push toteHandoffs).
 */
export interface ZoneHandoffSplit {
  /**
   * Next zone in `zoneSequence` after the parent's zone that contains at
   * least one of this FG's items. The continuation session is rooted here.
   */
  destinationZoneId: string;
  /**
   * Items the continuation owns. Verbatim copy of the FG's items on the
   * parent — picked-at-parent items keep their original zoneId AND their
   * pickEvents (so attribution survives the chain); deferred items keep
   * their downstream zoneId and remain untouched. The db layer is
   * responsible for assigning fresh `_id`s at insert time (same pattern as
   * the PROBLEM-session split already does).
   */
  continuationItems: PickSessionItem[];
  /**
   * `_id`s of items to remove from `parent.items[]` and append verbatim
   * to `parent.historicalItems[]`. Always equals every FG item on the
   * parent — the full FG migrates, regardless of whether individual items
   * were picked or deferred.
   */
  parentStripItemIds: string[];
  /**
   * Per-tote handoff records to push onto `parent.toteHandoffs[]`. One
   * entry per tote that is exclusive to this FG on the parent; shared
   * totes (multi-FG) are intentionally skipped — the multi-item validator
   * already forbids cross-FG tote sharing, so this only matters as a
   * defensive omission.
   */
  parentToteHandoffs: { toteId: string; destinationZoneId: string }[];
  /**
   * Tote ids exclusive to this FG on the parent, surfaced so the db layer
   * can repoint them to the continuation session via
   * `updateTotesPickSessionId`. Same set used to build `parentToteHandoffs`.
   */
  exclusiveToteIds: string[];
}

/**
 * Plan the zone-handoff continuation for one fulfillment group on a
 * zone-routed parent session that's about to be marked as picked. Returns
 * null when no continuation is needed: parent isn't zone-routed, the FG
 * has no items on the parent, the parent's zone isn't in `zoneSequence`,
 * or every FG item already belongs to the parent's zone (nothing to defer).
 *
 * Pure function — no DB IO. The db-util layer turns the returned plan into
 * Mongo writes: insert continuation, strip+archive items on parent, push
 * toteHandoffs, repoint totes, update the FG's pickStatus/pickSessionId.
 */
export function planZoneHandoffSplit({
  parent,
  fulfillmentGroupId,
  zoneSequence,
}: {
  parent: PickSession;
  fulfillmentGroupId: string;
  zoneSequence: string[];
}): ZoneHandoffSplit | null {
  if (parent.zoneId == null) {
    return null;
  }
  const parentZoneIndex = zoneSequence.indexOf(parent.zoneId);
  if (parentZoneIndex < 0) {
    return null;
  }
  const fgItems = parent.items.filter(
    (i) => i.fulfillmentGroupId === fulfillmentGroupId,
  );
  if (fgItems.length === 0) {
    return null;
  }

  // Downstream zones (strictly after parent zone in the configured sequence)
  // that actually contain any of this FG's items.
  const downstreamZones = new Set(zoneSequence.slice(parentZoneIndex + 1));
  const fgItemZones = new Set(
    fgItems
      .map((i) => i.zoneId)
      .filter((z): z is string => z != null && downstreamZones.has(z)),
  );
  if (fgItemZones.size === 0) {
    return null;
  }
  const destinationZoneId = zoneSequence
    .slice(parentZoneIndex + 1)
    .find((z) => fgItemZones.has(z));
  if (destinationZoneId === undefined) {
    return null;
  }

  const exclusiveToteIds = getToteIdsExclusiveToFulfillmentGroup(
    parent,
    fulfillmentGroupId,
  );
  return {
    destinationZoneId,
    continuationItems: fgItems.map((i) => ({ ...i })),
    parentStripItemIds: fgItems.map((i) => i._id),
    parentToteHandoffs: exclusiveToteIds.map((toteId) => ({
      toteId,
      destinationZoneId,
    })),
    exclusiveToteIds,
  };
}

export function getToteIdsForFulfillmentGroup(
  pickSession: PickSession,
  fulfillmentGroupId: string,
): string[] {
  const itemsForGroup = pickSession.items.filter(
    (item) => item.fulfillmentGroupId === fulfillmentGroupId,
  );
  // Dedupe: multiple items in the same FG sharing one tote would
  // otherwise list that tote once per item. Every caller treats the
  // return as a set (update tote.pickSessionId, build parentToteHandoffs,
  // etc.) so duplicates only produce visual artifacts — e.g. the same
  // tote rendered twice in the handoff placement modal.
  const totesForGroup = new Set(
    itemsForGroup.flatMap((item) => Object.keys(item.quantityByTote || {})),
  );
  return [...totesForGroup];
}

export function getToteIdsExclusiveToFulfillmentGroup(
  pickSession: PickSession,
  fulfillmentGroupId: string,
): string[] {
  const totesForGroup = getToteIdsForFulfillmentGroup(
    pickSession,
    fulfillmentGroupId,
  );
  const itemsNotInThisGroup = pickSession.items.filter(
    (item) => item.fulfillmentGroupId !== fulfillmentGroupId,
  );
  const totesForOtherGroups = itemsNotInThisGroup.flatMap((item) =>
    Object.keys(item.quantityByTote || {}),
  );
  return totesForGroup.filter((tote) => !totesForOtherGroups.includes(tote));
}

export function pickSessionHasAssignedTote(pickSession: PickSession): boolean {
  return pickSession.items.some(
    (item) =>
      item.quantityByTote && Object.keys(item.quantityByTote).length > 0,
  );
}

export function pickSessionHasPickedItems(pickSession: PickSession): boolean {
  return pickSession.items.some((item) => item.quantityPicked > 0);
}

export function allPickSessionItemsResolved(pickSession: PickSession): boolean {
  // Gate on in-scope items only. For zone-routed sessions, items destined
  // for a later zone are not the current picker's responsibility — they
  // get moved to a continuation session at split time. For non-zone
  // sessions this is identical to scanning all items (every item is in
  // scope by the matrix in `pickSessionItemIsInScope`).
  return pickSessionItemsInScope(pickSession).every((item) => {
    return item.quantityPicked + item.quantityOutOfStock >= item.totalQuantity;
  });
}

export function getExistingTotes(pickSession: PickSession): string[] {
  return Array.from(
    new Set(
      pickSession.items.flatMap((item) =>
        Object.keys(item.quantityByTote || {}),
      ),
    ),
  );
}

export function hasExistingTotes(pickSession: PickSession): boolean {
  return getExistingTotes(pickSession).length > 0;
}

export function addTotesToAllItemsInPickSession(
  items: PickSessionItem[],
  toteIds: string[],
  activeLotsByProduct?: PickSession["activeLotsByProduct"],
): PickSessionItem[] {
  const updatedItems: PickSessionItem[] = items.map((item) => {
    const emptyByTote = Object.fromEntries(
      toteIds.map((toteId) => [toteId, { quantity: 0 }]),
    );
    const quantityByTote = { ...emptyByTote, ...item.quantityByTote };

    // Picked before any tote was assigned: attribute the picked quantity to
    // the first tote, split across the lots the pick events recorded.
    const attributedQuantity = Object.values(quantityByTote).reduce(
      (sum, entry) => sum + entry.quantity,
      0,
    );
    const firstToteId = Object.keys(quantityByTote)[0];
    const activeLot = item.productId
      ? activeLotsByProduct?.[item.productId]
      : undefined;
    if (item.quantityPicked > 0 && attributedQuantity === 0 && firstToteId) {
      const lotQuantities = splitQuantityByLotFromPickEvents({
        pickEvents: item.pickEvents,
        quantity: item.quantityPicked,
        fallbackLot: activeLot,
      });
      if (Object.keys(lotQuantities).length > 0) {
        return {
          ...item,
          quantityByTote: {
            ...quantityByTote,
            [firstToteId]: { quantity: item.quantityPicked },
          },
          lotQuantityByTote: { [firstToteId]: lotQuantities },
        };
      }
    }

    return { ...item, quantityByTote };
  });

  return updatedItems;
}

/** Mirrors `quantityByTote`: a mutator that moves or drops a tote's quantity
 * must apply the same transformation to the picker-entered lot map. */
export type LotQuantityByTote = NonNullable<
  PickSessionItem["lotQuantityByTote"]
>;

export type LotQuantities = LotQuantityByTote[string];

export interface LotPickEvent {
  quantity: number;
  inventoryItemId?: string | null;
  lotNumber?: string | null;
}

type PickedLot = { inventoryItemId: string; lotNumber: string } | null;

function lotOfEvent(event: LotPickEvent): PickedLot {
  return event.inventoryItemId && event.lotNumber
    ? { inventoryItemId: event.inventoryItemId, lotNumber: event.lotNumber }
    : null;
}

/**
 * Replay the pick log into the units still picked, oldest first. A positive
 * event adds units on its lot (or no lot); a negative event (an un-pick)
 * removes the newest units of its lot, or of any lot when it carries none, and
 * is clamped at what the lot still holds.
 */
function replayPickedUnits(
  pickEvents: LotPickEvent[] | null | undefined,
): PickedLot[] {
  const units: PickedLot[] = [];
  for (const event of pickEvents ?? []) {
    const lot = lotOfEvent(event);
    if (event.quantity > 0) {
      for (let i = 0; i < event.quantity; i++) {
        units.push(lot);
      }
      continue;
    }
    let toRemove = -event.quantity;
    for (let i = units.length - 1; i >= 0 && toRemove > 0; i--) {
      if (lot && units[i]?.inventoryItemId !== lot.inventoryItemId) {
        continue;
      }
      units.splice(i, 1);
      toRemove--;
    }
  }
  return units;
}

/** Units the log currently accounts for — the baseline a resolution delta is
 * measured against, so a retried write after a failed one is not a no-op. */
export function countPickedUnitsFromPickEvents(
  pickEvents: LotPickEvent[] | null | undefined,
): number {
  return replayPickedUnits(pickEvents).length;
}

/**
 * Attribute `quantity` units to lots by replaying the item's pick events,
 * oldest first, after skipping `offset` units an earlier attribution consumed.
 * Units no lot-carrying event covers go to `fallbackLot`.
 *
 * Un-picks are logged as negative events, so the replay normally covers
 * exactly `quantity` units. The cap only matters for logs written before
 * un-picks were recorded (OMS-3873), where the newest units are the ones
 * dropped — the assumption being that a picker put back what they most
 * recently picked.
 */
export function splitQuantityByLotFromPickEvents({
  pickEvents,
  quantity,
  offset = 0,
  fallbackLot,
}: {
  pickEvents: LotPickEvent[] | null | undefined;
  quantity: number;
  offset?: number;
  fallbackLot?: { inventoryItemId: string; lotNumber: string } | null;
}): LotQuantities {
  const split: LotQuantities = {};
  const add = (
    lot: { inventoryItemId: string; lotNumber: string },
    units: number,
  ) => {
    split[lot.inventoryItemId] = {
      lotNumber: lot.lotNumber,
      quantity: (split[lot.inventoryItemId]?.quantity ?? 0) + units,
    };
  };

  let remaining = quantity;
  for (const unit of replayPickedUnits(pickEvents).slice(offset)) {
    if (remaining <= 0) {
      break;
    }
    const lot = unit ?? fallbackLot;
    if (!lot) {
      continue;
    }
    add(lot, 1);
    remaining -= 1;
  }
  if (remaining > 0 && fallbackLot) {
    add(fallbackLot, remaining);
  }
  return split;
}

/**
 * Lot slices for an un-pick of `quantity` units, as negative events to append
 * to the log. Units come off the newest lot first; a run of same-lot units
 * collapses into one event. Units the log does not cover (a legacy gap) come
 * off with no lot.
 */
export function attributeUnpickToLots({
  pickEvents,
  quantity,
}: {
  pickEvents: LotPickEvent[] | null | undefined;
  quantity: number;
}): LotPickEvent[] {
  const slices: LotPickEvent[] = [];
  const remove = (lot: PickedLot) => {
    const last = slices[slices.length - 1];
    if (
      last &&
      (last.inventoryItemId ?? null) === (lot?.inventoryItemId ?? null)
    ) {
      last.quantity -= 1;
      return;
    }
    slices.push({
      quantity: -1,
      ...(lot && {
        inventoryItemId: lot.inventoryItemId,
        lotNumber: lot.lotNumber,
      }),
    });
  };

  const units = replayPickedUnits(pickEvents);
  let remaining = quantity;
  for (let i = units.length - 1; i >= 0 && remaining > 0; i--) {
    remove(units[i] ?? null);
    remaining -= 1;
  }
  for (; remaining > 0; remaining--) {
    remove(null);
  }
  return slices;
}

/** Returns the input reference when nothing matched, so callers can detect
 * "no change" by identity and skip the write. */
function filterLotQuantityByTote(
  lotQuantityByTote: LotQuantityByTote | null | undefined,
  keep: (toteId: string) => boolean,
): LotQuantityByTote | null | undefined {
  if (!lotQuantityByTote) {
    return lotQuantityByTote;
  }
  const entries = Object.entries(lotQuantityByTote).filter(([toteId]) =>
    keep(toteId),
  );
  if (entries.length === Object.keys(lotQuantityByTote).length) {
    return lotQuantityByTote;
  }
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/** Drops the given totes' lot entries; empties collapse to `null`, mirroring
 * how `quantityByTote` is nulled. */
export function removeLotEntriesForTotes(
  lotQuantityByTote: LotQuantityByTote | null | undefined,
  toteIds: readonly string[],
): LotQuantityByTote | null | undefined {
  const removed = new Set(toteIds);
  return filterLotQuantityByTote(
    lotQuantityByTote,
    (toteId) => !removed.has(toteId),
  );
}

/** Moves one tote's lot entries onto another, summing per inventory item when
 * the destination already holds that lot (its `lotNumber` wins). */
export function moveLotEntriesBetweenTotes(
  lotQuantityByTote: LotQuantityByTote | null | undefined,
  fromToteId: string,
  toToteId: string,
): LotQuantityByTote | null | undefined {
  const fromEntries = lotQuantityByTote?.[fromToteId];
  if (!lotQuantityByTote || !fromEntries || fromToteId === toToteId) {
    return lotQuantityByTote;
  }
  const { [fromToteId]: _moved, ...rest } = lotQuantityByTote;
  const merged: LotQuantityByTote[string] = { ...rest[toToteId] };
  for (const [inventoryItemId, entry] of Object.entries(fromEntries)) {
    const existing = merged[inventoryItemId];
    merged[inventoryItemId] = existing
      ? {
          lotNumber: existing.lotNumber,
          quantity: existing.quantity + entry.quantity,
        }
      : entry;
  }
  return { ...rest, [toToteId]: merged };
}

/** Folds every tote's lot entries onto one tote (merging like
 * `moveLotEntriesBetweenTotes`); returns the input reference when unchanged. */
export function consolidateLotEntriesOntoTote(
  lotQuantityByTote: LotQuantityByTote | null | undefined,
  toteId: string,
): LotQuantityByTote | null | undefined {
  let result = lotQuantityByTote;
  for (const fromToteId of Object.keys(lotQuantityByTote ?? {})) {
    result = moveLotEntriesBetweenTotes(result, fromToteId, toteId);
  }
  return result;
}
